<template lang="pug">
  .rma-steps-scroll.rogue-scroll-x
    .rma-step-navs
      router-link.nav-tab(
        :to="{ path: '/rmaprocess/initiate', query: $route.query }"
        :class="{'passed': $route.name !== 'rmaInitiate'}"
      )
        Icon.check-icon(
          name="check"
          v-if="validInit"
          :fill="$route.name === 'rmaInitiate' ? 'white' : 'green'"
        )
        | {{ __('Initiate') }}
        span.mobile-hidden {{ __('Return') }}
      component.nav-tab(
        :is="validInit ? 'router-link' : 'span'"
        :to="{ path: '/rmaprocess/select-items', query: $route.query }"
        :class="{'disabled': !validInit}"
      )
        Icon.check-icon(
          name="check"
          v-if="validItemsSelect"
          :fill="$route.name === 'rmaSelectItems' ? 'white' : 'green'"
        )
        | {{ __('Select') }}
        span.mobile-hidden {{ __('Items for Return') }}
      component.nav-tab(
        :is="validItemsSelect ? 'router-link' : 'span'"
        :to="{ path: '/rmaprocess/confirm', query: $route.query }"
        :class="{'disabled': !validItemsSelect}"
      )
        Icon.check-icon(
          name="check"
          v-if="validInit && validItemsSelect && $route.name === 'rmaPrintReturn'"
          :fill="$route.name === 'rmaConfirm' ? 'white' : 'green'"
        )
        | {{ __('Confirm') }}
        span.mobile-hidden {{ __('Return Request') }}
      component.nav-tab(
        :is="validConfirm ? 'router-link' : 'span'"
        :to="{ path: '/rmaprocess/print', query: $route.query }"
        :class="{'disabled': !validConfirm}"
      )
        | {{ __('Print') }}
        span.mobile-hidden {{ __('Return Form') }}
</template>
<script setup lang="ts">
import { computed, nextTick, watch, ref } from 'vue'
import { useRoute } from 'vue-router'
// no email/order validation here; gating is controlled by route state only
import { useRogue } from '@/js/services/Rogue'

type Props = { data: any }
const props = defineProps<Props>()

const route = useRoute()
const rogue = useRogue()

const hasVisitedSelectItems = ref(['rmaSelectItems', 'rmaConfirm', 'rmaPrintReturn'].includes(String(route.name)))
const validInit = computed(() => hasVisitedSelectItems.value)

const validItemsSelect = computed(() => {
  const { name, phone, returnItems } = props.data || {}
  if (!name || !phone || !returnItems?.length) {
    return false
  }

  return returnItems.every((item: any) => (
    Boolean(item.returnType === 'exchange' && item.returnReason && item.returnComment)
    || Boolean(item.returnType === 'return' && item.returnReason)
  ))
})

const validConfirm = computed(() => route.name === 'rmaPrintReturn' || props.data?.rmaSuccess)

watch(() => route.name, (name) => {
  if (['rmaSelectItems', 'rmaConfirm', 'rmaPrintReturn'].includes(String(name))) {
    hasVisitedSelectItems.value = true
  }
  if (!rogue.ssr && name === 'rmaPrintReturn') {
    nextTick(() => {
      const scroll = document.querySelector('.rma-steps-scroll') as HTMLElement | null
      const navs = document.querySelector('.rma-step-navs') as HTMLElement | null
      if (scroll && navs) {
        scroll.scrollLeft = navs.scrollWidth
      }
    })
  }
})
</script>

<style lang="scss" scoped>
$tip-size: $space-xs;

.mobile-hidden {
  display: none;

  @include not-mobile {
    display: inline-block;
    margin-left: $space-xxxs;
  }
}

.nav-tab {
  display: flex;
  justify-content: center;
  position: relative;
  padding: $space-xs $space-xs;
  text-transform: uppercase;
  font-weight: $weight-bold;
  font-size: $size-xxs;
  font-family: $font-condensed;
  text-decoration: none;
  cursor: not-allowed;
  border: $border-width-thin solid $color-gray;
  border-left: none;
  color: $color-gray-dark;
  flex-grow: 1;
  white-space: nowrap;
  z-index: 1;

@include all-desktop() {
    flex-grow: 0;
    font-size: $size-xs;
    padding: $space-xs $space-xl;
  }

  &:not(.disabled) {
    background-color: $color-gray-light;
    border-color: $color-gray-light;
    color: $color-black;
    cursor: pointer;
  }

  &.inactive {
    color: $color-gray;
    background: $color-white;
    pointer-events: none;
    user-select: none;
    cursor: inherit;
  }

  &.router-link-active {
    background: $color-black;
    color: $color-white;
    z-index: 1;
    border-top: $border-width-thin solid $color-black;
    border-bottom: $border-width-thin solid $color-black;

    &:after {
      content: '';
      width: 0;
      height: 0;
      left: 50%;
      position: absolute;
      transform: translateX(-50%);
      border-left: $tip-size solid transparent;
      border-right: $tip-size solid transparent;
      border-top: $tip-size solid $color-black;
      bottom: (-$tip-size) + 1;
    }
  }
}

.check-icon {
  margin-right: $space-xxs;
  height: $size-xxs;
  width: $size-xxs;

@include all-desktop() {
    height: $size-xs;
    width: $size-xs;
  }
}

.rma-step-navs {
  display: flex;
  margin-bottom: $space-m + $tip-size;

 @include all-desktop() {
    padding: 0;
    position: relative;

    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 1px;
      background: $color-gray;
    }

    > div {
      &, &:first-of-type, &:last-of-type {
        flex-grow: 0;
      }
    }
  }
}
</style>

